import { clsx } from 'clsx';
import { format } from 'date-fns';
import React, { useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import { ConnectedFacilityPublic, ShiftTypesEnum, ShiftRateTypesEnum } from '../../../../../js/api';
import { useWindowSize } from '../../../../../js/app/hooks';
import { Button } from '../../../components/button';
import { Checkbox } from '../../../components/checkbox';
import { Dialog, DialogTitle, DialogBody } from '../../../components/dialog';
import { Field, Label, FieldGroup } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { TooltippedText } from '../../../components/tooltipped-text';
import { formatTimeStringForInput } from '../../../utils/time';
import { SHIFT_TYPE_LABELS, SHIFT_TYPE_ICONS, SHIFT_TYPE_STYLES } from '../../../utils/utils';

export interface DateShiftConfig {
  shiftType: ShiftTypesEnum | null;
  onCall: boolean;
  facility: { id: string; name: string } | null;
  bonus_amount?: number;
  holiday_amount?: number;
}

export interface ShiftSettings {
  shift_length: number;
  shift_start_day: string;
  shift_start_night: string;
  shift_start_24h: string;
}

interface ShiftConfigurationProps {
  connectedFacility: ConnectedFacilityPublic | null;
  selectedDates: Date[];
  shiftConfigs: Record<string, DateShiftConfig>;
  onShiftConfigChange: (config: Record<string, DateShiftConfig>) => void;
  onPreviewSchedule: (
    config: Record<string, DateShiftConfig>,
    shiftSettings: ShiftSettings
  ) => void;
  onRemoveAllDates: () => void;
  onChangeFacility?: (dateKeys: string[]) => void;
}

const getSelectedDateShiftTypeButtonColors = (
  shiftType: ShiftTypesEnum,
  isSelected: boolean
): 'amber-light' | 'blue-light' | 'green-light' | 'dark/zinc' | 'zinc-light' => {
  const baseColor = 'zinc-light' as const;
  switch (shiftType) {
    case ShiftTypesEnum.DAY:
      return isSelected ? 'amber-light' : baseColor;
    case ShiftTypesEnum.NIGHT:
      return isSelected ? 'blue-light' : baseColor;
    case ShiftTypesEnum._24H:
      return isSelected ? 'green-light' : baseColor;
    default:
      return isSelected ? 'dark/zinc' : baseColor;
  }
};

const getSelectAllShiftTypeButtonColors = (
  shiftType: ShiftTypesEnum
): 'amber-outline' | 'blue-outline' | 'green-outline' | 'zinc-outline' => {
  switch (shiftType) {
    case ShiftTypesEnum.DAY:
      return 'amber-outline';
    case ShiftTypesEnum.NIGHT:
      return 'blue-outline';
    case ShiftTypesEnum._24H:
      return 'green-outline';
    default:
      return 'zinc-outline';
  }
};

const DATE_KEY_FORMAT = 'yyyy-MM-dd';

export const ShiftConfiguration: React.FC<ShiftConfigurationProps> = ({
  connectedFacility,
  selectedDates,
  shiftConfigs,
  onShiftConfigChange,
  onPreviewSchedule,
  onRemoveAllDates,
  onChangeFacility,
}) => {
  const [selectedDateKeys, setSelectedDateKeys] = useState<Set<string>>(new Set());
  const { width } = useWindowSize();
  const isSmallScreen = width < 868; // ml breakpoint

  // Local state for editable shift settings (initialized from connectedFacility defaults)
  const [shiftSettings, setShiftSettings] = useState<ShiftSettings>({
    shift_length: connectedFacility?.shift_length || 12,
    shift_start_day: connectedFacility?.shift_start_day || '07:00:00',
    shift_start_night: connectedFacility?.shift_start_night || '19:00:00',
    shift_start_24h: connectedFacility?.shift_start_24h || '07:00:00',
  });

  // Update local state when connectedFacility changes (e.g., when facility is changed)
  useEffect(() => {
    if (connectedFacility) {
      setShiftSettings({
        shift_length: connectedFacility.shift_length || 12,
        shift_start_day: connectedFacility.shift_start_day || '07:00:00',
        shift_start_night: connectedFacility.shift_start_night || '19:00:00',
        shift_start_24h: connectedFacility.shift_start_24h || '07:00:00',
      });
    }
  }, [connectedFacility]);

  // Get available shift types from connected facility
  const availableShiftTypes = useMemo(() => {
    if (!connectedFacility?.shift_rate_types) return [];

    const isShiftType = (type: any): type is ShiftTypesEnum =>
      Object.values(ShiftTypesEnum).includes(type);

    return connectedFacility.shift_rate_types.reduce<ShiftTypesEnum[]>((acc, type) => {
      if (isShiftType(type)) {
        acc.push(type);
      }
      return acc;
    }, []);
  }, [connectedFacility]);

  // using ShiftRateTypesEnum here because that is the generated equivalent of
  // what the backend uses to include "call" in shift_rate_types
  // Eventually, 'on call' will be a shift modifier instead of an actual shift type
  // so we'll need to modify this conditional
  const hasCallRate = useMemo(() => {
    // If shift_rate_types doesn't exist, show the checkbox (same logic as availableShiftTypes)
    if (!connectedFacility?.shift_rate_types) {
      return true;
    }
    // If shift_rate_types exists, only show if it includes CALL
    return connectedFacility.shift_rate_types.includes(ShiftRateTypesEnum.CALL);
  }, [connectedFacility]);

  // Bonus modal state
  const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
  const [bonusInput, setBonusInput] = useState('');

  const handleBonusConfirm = () => {
    const amount = parseInt(bonusInput, 10) || 0;
    const newConfigs = { ...shiftConfigs };
    const keys = Array.from(selectedDateKeys);
    keys.forEach((key) => {
      const existing = newConfigs[key] || { shiftType: null, onCall: false, facility: null };
      newConfigs[key] = { ...existing, bonus_amount: amount };
    });
    onShiftConfigChange(newConfigs);
    setIsBonusModalOpen(false);
    setBonusInput('');
  };

  // Holiday pay modal state
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [holidayInput, setHolidayInput] = useState('');

  const handleHolidayConfirm = () => {
    const amount = parseInt(holidayInput, 10) || 0;
    const newConfigs = { ...shiftConfigs };
    const keys = Array.from(selectedDateKeys);
    keys.forEach((key) => {
      const existing = newConfigs[key] || { shiftType: null, onCall: false, facility: null };
      newConfigs[key] = { ...existing, holiday_amount: amount };
    });
    onShiftConfigChange(newConfigs);
    setIsHolidayModalOpen(false);
    setHolidayInput('');
  };

  const defaultConfig: DateShiftConfig = { shiftType: null, onCall: false, facility: null };

  const updateShiftConfig = (dateKey: string, config: Partial<DateShiftConfig>) => {
    const existing = shiftConfigs[dateKey] || defaultConfig;
    const updated = { ...existing, ...config };
    const newConfigs = { ...shiftConfigs, [dateKey]: updated };
    onShiftConfigChange(newConfigs);
  };

  const applyShiftTypeToAll = (shiftType: ShiftTypesEnum | null) => {
    const newConfigs = { ...shiftConfigs };
    selectedDates.forEach((date) => {
      const dateKey = format(date, DATE_KEY_FORMAT);
      const existing = newConfigs[dateKey] || defaultConfig;
      newConfigs[dateKey] = { ...existing, shiftType };
    });
    onShiftConfigChange(newConfigs);
  };

  // Clear all shift types and onCall status
  const clearAllShiftTypes = () => {
    const newConfigs = { ...shiftConfigs };
    selectedDates.forEach((date) => {
      const dateKey = format(date, DATE_KEY_FORMAT);
      const existing = newConfigs[dateKey];
      newConfigs[dateKey] = {
        shiftType: null,
        onCall: false,
        facility: existing?.facility || null,
      };
    });
    onShiftConfigChange(newConfigs);
  };

  // Toggle onCall for all selected dates
  const toggleOnCallForAll = () => {
    const newConfigs = { ...shiftConfigs };
    const allOnCall = selectedDates.every((date) => {
      const dateKey = format(date, DATE_KEY_FORMAT);
      const existing = newConfigs[dateKey];
      return existing?.onCall === true;
    });
    const newOnCallValue = !allOnCall;

    selectedDates.forEach((date) => {
      const dateKey = format(date, DATE_KEY_FORMAT);
      const existing = newConfigs[dateKey] || { shiftType: null, onCall: false };
      newConfigs[dateKey] = { ...existing, onCall: newOnCallValue };
    });
    onShiftConfigChange(newConfigs);
  };

  // Check if selected dates and all selected dates have a shift type selected
  const allShiftsHaveType = useMemo(() => {
    if (!selectedDates.length) return false;
    return selectedDates.every((date) => {
      const dateKey = format(date, DATE_KEY_FORMAT);
      const config = shiftConfigs[dateKey];
      return config?.shiftType !== null && config?.shiftType !== undefined;
    });
  }, [selectedDates, shiftConfigs]);

  if (!connectedFacility) {
    return null;
  }

  return (
    <div className="tw:md:bg-white tw:md:rounded-xl tw:border-t tw:pt-3 tw:mt-2 tw:md:mt-0 tw:md:p-4 tw:md:border tw:border-zinc-200">
      <h2 className="tw:!text-base tw:!font-semibold tw:!text-brand-green-0 tw:!mb-4">
        Shift Configuration
        <span className="tw:text-xs tw:block tw:text-zinc-500 tw:font-medium tw:italic">
          This information is used to generate the schedule. You will be able to edit individual
          shift times on the Preview Schedule page.
        </span>
      </h2>

      {/* Shift Configuration Fields */}
      <FieldGroup className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-4 tw:gap-x-4 tw:gap-y-0 tw:mb-0">
        <Field className="tw:mb-2">
          <Label>Shift Length (Hours)</Label>
          <Input
            type="number"
            value={shiftSettings.shift_length?.toString()}
            onChange={(e) => {
              setShiftSettings({
                ...shiftSettings,
                shift_length: parseInt(e.target.value, 10) || 0,
              });
            }}
            className="tw:w-full"
            small={isSmallScreen}
          />
        </Field>
        <Field className="tw:mb-2">
          <Label>Day Shift Start Time</Label>
          <Input
            type="time"
            value={formatTimeStringForInput(shiftSettings.shift_start_day)}
            onChange={(e) => {
              setShiftSettings({
                ...shiftSettings,
                shift_start_day: e.target.value + ':00',
              });
            }}
            className="tw:w-full"
            small={isSmallScreen}
          />
        </Field>
        <Field className="tw:mb-2">
          <Label>Night Shift Start Time</Label>
          <Input
            type="time"
            value={formatTimeStringForInput(shiftSettings.shift_start_night)}
            onChange={(e) => {
              setShiftSettings({
                ...shiftSettings,
                shift_start_night: e.target.value + ':00',
              });
            }}
            className="tw:w-full"
            small={isSmallScreen}
          />
        </Field>
        <Field className="tw:mb-2">
          <Label>24 Hr Shift Start Time</Label>
          <Input
            type="time"
            value={formatTimeStringForInput(shiftSettings.shift_start_24h)}
            onChange={(e) => {
              setShiftSettings({
                ...shiftSettings,
                shift_start_24h: e.target.value + ':00',
              });
            }}
            className="tw:w-full"
            small={isSmallScreen}
          />
        </Field>
      </FieldGroup>

      {/* Selected Days */}
      <div>
        {/* Shift Type Buttons - Apply to All */}
        <div className=" tw:border-zinc-200 tw:pr-4.5 tw:pl-2">
          <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-2 tw:my-3">
            <div className="tw:text-xs tw:text-zinc-500">
              You can click a shift type button to apply it to all dates and/or set each date&#39;s
              shift type individually and then preview the schedule
            </div>
            <div className="tw:flex tw:flex-wrap tw:justify-center tw:sm:justify-end tw:gap-2 tw:mb-2">
              <Button type="button" outline small onClick={clearAllShiftTypes}>
                <i className="far fa-fw fa-recycle tw:text-stoplight-danger" />
                Clear
              </Button>
              {availableShiftTypes.map((shiftType) => (
                <Button
                  key={shiftType}
                  type="button"
                  small
                  color={getSelectAllShiftTypeButtonColors(shiftType)}
                  onClick={() => applyShiftTypeToAll(shiftType)}
                >
                  <i className={`fas ${SHIFT_TYPE_ICONS[shiftType]}`} />
                  {SHIFT_TYPE_LABELS[shiftType]}
                </Button>
              ))}
              {hasCallRate && (
                <Button type="button" small color="zinc-outline" onClick={toggleOnCallForAll}>
                  On Call
                </Button>
              )}
            </div>
          </div>
        </div>
        <h2 className="tw:flex tw:justify-between tw:items-center tw:!text-base tw:!font-semibold tw:!text-white tw:!mb-0 tw:bg-brand-green-0 tw:p-2 tw:rounded-lg">
          Selected Dates
          <div className="tw:flex tw:gap-2">
            {onChangeFacility && selectedDates.length > 0 && (
              <Button
                type="button"
                small
                color="brand-green-outline-on-dark"
                onClick={() => {
                  if (selectedDateKeys.size === 0) {
                    toast.info(
                      'No dates have been selected. Use the checkboxes to select the dates you want to change.'
                    );
                    return;
                  }
                  onChangeFacility(Array.from(selectedDateKeys));
                }}
              >
                Change Facility
              </Button>
            )}
            {selectedDates.length > 0 && (
              <Button
                type="button"
                small
                color="brand-green-outline-on-dark"
                onClick={() => {
                  if (selectedDateKeys.size === 0) {
                    toast.info(
                      'No dates have been selected. Use the checkboxes to select the dates you want to change.'
                    );
                    return;
                  }
                  setBonusInput('');
                  setIsBonusModalOpen(true);
                }}
              >
                Add Bonus
              </Button>
            )}
            {selectedDates.length > 0 && (
              <Button
                type="button"
                small
                color="brand-green-outline-on-dark"
                onClick={() => {
                  if (selectedDateKeys.size === 0) {
                    toast.info(
                      'No dates have been selected. Use the checkboxes to select the dates you want to change.'
                    );
                    return;
                  }
                  setHolidayInput('');
                  setIsHolidayModalOpen(true);
                }}
              >
                Add Holiday Pay
              </Button>
            )}
            {selectedDates.length > 0 && (
              <Button
                type="button"
                small
                color="brand-green-outline-on-dark"
                onClick={onRemoveAllDates}
              >
                Remove All
              </Button>
            )}
          </div>
        </h2>
        <div>
          {selectedDates.length === 0 ? (
            <div className="tw:w-full tw:p-8 tw:bg-zinc-50 tw:border tw:border-dashed tw:border-zinc-200 tw:rounded-xl tw:text-center tw:my-4">
              <p className="tw:text-sm tw:text-zinc-500 tw:!mb-0">
                To add days, simply select them on the calendar above and they will appear here
              </p>
            </div>
          ) : (
            selectedDates.map((date) => {
              const dateKey = format(date, DATE_KEY_FORMAT);
              const config = shiftConfigs[dateKey] || {
                shiftType: null,
                onCall: false,
                facility: null,
              };
              const dateFormattedLarge = format(date, 'EEEE, MMMM d, yyyy');
              const dateFormattedSmall = format(date, 'EEE MM/d/yyyy');

              return (
                <div
                  key={dateKey}
                  className="tw:flex tw:justify-between tw:items-center tw:border-b tw:border-zinc-200 tw:p-3 tw:last:border-b-0"
                >
                  <div className="tw:flex tw:items-center tw:gap-2">
                    {onChangeFacility && (
                      <Checkbox
                        checked={selectedDateKeys.has(dateKey)}
                        onChange={(checked) => {
                          setSelectedDateKeys((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(dateKey);
                            else next.delete(dateKey);
                            return next;
                          });
                        }}
                        color="brandgreen"
                      />
                    )}
                    <div>
                      <span className="tw:hidden tw:md:block tw:text-sm tw:font-medium">
                        {dateFormattedLarge}
                      </span>
                      <span className="tw:block tw:md:hidden tw:text-xs tw:font-medium">
                        {dateFormattedSmall}
                      </span>
                      {config.facility && (
                        <span className="tw:text-xs tw:text-zinc-400">{config.facility.name}</span>
                      )}
                      {config.bonus_amount != null && config.bonus_amount > 0 && (
                        <span
                          className={twMerge(
                            clsx(
                              'tw:text-xs  tw:font-medium tw:ml-2',
                              SHIFT_TYPE_STYLES[config.shiftType || ShiftTypesEnum.DAY].color
                            )
                          )}
                        >
                          <i className="fas fa-gift tw:mr-1" />$
                          {config.bonus_amount?.toLocaleString()} bonus
                        </span>
                      )}
                      {config.holiday_amount != null && config.holiday_amount > 0 && (
                        <span
                          className={twMerge(
                            clsx(
                              'tw:text-xs  tw:font-medium tw:ml-2',
                              SHIFT_TYPE_STYLES[config.shiftType || ShiftTypesEnum.DAY].color
                            )
                          )}
                        >
                          <i className="fas fa-star tw:mr-1" />$
                          {config.holiday_amount?.toLocaleString()} holiday
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Shift Type Radio Buttons */}
                  <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                    {availableShiftTypes.map((shiftType) => (
                      <Button
                        key={shiftType}
                        type="button"
                        small
                        onClick={() => updateShiftConfig(dateKey, { shiftType })}
                        color={getSelectedDateShiftTypeButtonColors(
                          shiftType,
                          config.shiftType === shiftType
                        )}
                      >
                        <i className={`fas ${SHIFT_TYPE_ICONS[shiftType]}`} />
                        {SHIFT_TYPE_LABELS[shiftType]}
                      </Button>
                    ))}
                    {/* On Call Checkbox */}
                    {hasCallRate && (
                      <Field>
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <Checkbox
                            checked={config.onCall}
                            onChange={(checked) => updateShiftConfig(dateKey, { onCall: checked })}
                            color="brandgreen"
                            id={`on-call-${dateKey}`}
                          />
                          <Label
                            htmlFor={`on-call-${dateKey}`}
                            className={twMerge(
                              'tw:!mb-0 tw:cursor-pointer tw:!text-xs',
                              config.onCall ? 'tw:!text-stoplight-danger' : 'tw:text-zinc-500'
                            )}
                          >
                            On Call
                          </Label>
                        </div>
                      </Field>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="tw:flex tw:justify-end tw:mt-4 tw:items-center">
          {!allShiftsHaveType && (
            <div>
              <TooltippedText tooltipContent="Select at least 1 date and ensure all dates have a shift type to enable the Preview Schedule button">
                <i className="far fa-fw fa-info-circle tw:text-brand-green-0/50 tw:mr-2" />
              </TooltippedText>
            </div>
          )}
          <Button
            type="button"
            color="brand-green-outline"
            onClick={() => onPreviewSchedule(shiftConfigs, shiftSettings)}
            disabled={!allShiftsHaveType}
          >
            Preview Schedule
            <i className="far fa-fw fa-arrow-right" />
          </Button>
        </div>
      </div>

      {/* Bonus Amount Modal */}
      <Dialog open={isBonusModalOpen} onClose={() => setIsBonusModalOpen(false)} size="sm">
        <DialogTitle>Add Shift Bonus</DialogTitle>
        <DialogBody>
          <p className="tw:text-sm tw:text-zinc-500 tw:mb-3">
            Enter a bonus amount to apply to {selectedDateKeys.size > 0 ? 'selected' : 'all'} dates.
            Enter 0 to clear existing bonuses.
          </p>
          <Field>
            <Label>Bonus Amount ($)</Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={bonusInput}
              onChange={(e) => setBonusInput(e.target.value)}
              placeholder="0"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleBonusConfirm();
              }}
            />
          </Field>
          <div className="tw:flex tw:justify-end tw:gap-2 tw:mt-4">
            <Button type="button" outline onClick={() => setIsBonusModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" color="brandgreen0" onClick={handleBonusConfirm}>
              Apply
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Holiday Pay Modal */}
      <Dialog open={isHolidayModalOpen} onClose={() => setIsHolidayModalOpen(false)} size="sm">
        <DialogTitle>Add Holiday Pay</DialogTitle>
        <DialogBody>
          <p className="tw:text-sm tw:text-zinc-500 tw:mb-3">
            Enter a holiday pay amount to apply to {selectedDateKeys.size > 0 ? 'selected' : 'all'}{' '}
            dates. Enter 0 to clear existing holiday pay.
          </p>
          <Field>
            <Label>Holiday Pay Amount ($)</Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={holidayInput}
              onChange={(e) => setHolidayInput(e.target.value)}
              placeholder="0"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleHolidayConfirm();
              }}
            />
          </Field>
          <div className="tw:flex tw:justify-end tw:gap-2 tw:mt-4">
            <Button type="button" outline onClick={() => setIsHolidayModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" color="brandgreen0" onClick={handleHolidayConfirm}>
              Apply
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </div>
  );
};
